"use client";
import React, { useMemo, useCallback, useEffect, useState } from "react";
import { InfiniteScrollDetector } from "../infiniteScroll";
import { Loading } from "../loading";
import { VirtualGalleryProps, GalleryItem } from "./types";
import { VirtualGalleryItem } from "./VirtualGalleryItem";
import { VisibleItemsManager } from "./VisibleItemsManager";

/**
 * 虚拟Gallery组件，支持从左到右排列并自动换行
 * 实现了虚拟滚动和无限加载功能
 */
export const VirtualGallery = <T extends GalleryItem = GalleryItem>(props: VirtualGalleryProps<T>) => {
    const {
        data,
        renderItem,
        itemWidth,
        itemHeight,
        columnGap = 12,
        rowGap = 12,
        padding = 16,
        preloadItemCount = 15,
        enableInfiniteScroll = false,
        onLoadMore,
        loadingMore = false,
        hasMore = true,
        loadMoreThreshold = 100,
        loadingMoreContent,
        noMoreContent,
        style,
        itemStyle,
        className,
    } = props;

    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // 使用 useMemo 缓存 visibleItemsManager 实例，避免不必要的重新渲染
    const visibleItemsManager = useMemo(() => {
        return new VisibleItemsManager();
    }, []);

    // 监听容器宽度变化
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const paddingNum = typeof padding === 'string' ? parseInt(padding) || 0 : padding;
                const availableWidth = containerRef.current.clientWidth - paddingNum * 2;
                setContainerWidth(availableWidth);
            }
        };

        updateWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [padding]);

    // 计算每行能放多少个项目
    const itemsPerRow = useMemo(() => {
        if (containerWidth <= 0) { return 1; }
        return Math.floor((containerWidth + columnGap) / (itemWidth + columnGap)) || 1;
    }, [containerWidth, itemWidth, columnGap]);

    // 计算总行数
    const totalRows = useMemo(() => {
        return Math.ceil(data.length / itemsPerRow);
    }, [data.length, itemsPerRow]);

    // 获取项目的位置信息
    const getItemPosition = useCallback((index: number) => {
        const row = Math.floor(index / itemsPerRow);
        const col = index % itemsPerRow;
        
        // 当只有一列时，计算居中的x坐标
        let x = col * (itemWidth + columnGap);
        if (itemsPerRow === 1 && containerWidth > itemWidth) {
            // 计算居中位置：(容器宽度 - 项目宽度) / 2
            x = (containerWidth - itemWidth) / 2;
        }

        return {
            x,
            y: row * (itemHeight + rowGap),
            row,
            col,
        };
    }, [itemsPerRow, itemWidth, itemHeight, columnGap, rowGap, containerWidth]);

    // 默认的加载内容
    const defaultLoadingContent = useMemo(() => (
        <Loading className="mt-3" fill="#666" />
    ), []);

    // 容器样式
    const containerStyle: React.CSSProperties = useMemo(() => ({
        position: 'relative',
        width: '100%',
        height: totalRows * (itemHeight + rowGap) - rowGap,
        padding: typeof padding === 'string' ? padding : `${padding}px`,
        ...style,
    }), [totalRows, itemHeight, rowGap, padding, style]);

    return (
        <div className={className} style={{ width: '100%' }}>
            <div
                ref={containerRef}
                style={containerStyle}
            >
                {data.map((item, index) => {
                    const position = getItemPosition(index);

                    return (
                        <VirtualGalleryItem
                            key={item.id}
                            dataIndex={index}
                            itemWidth={itemWidth}
                            itemHeight={itemHeight}
                            preloadItemCount={preloadItemCount}
                            visibleItemsManager={visibleItemsManager}
                            style={{
                                position: 'absolute',
                                left: position.x,
                                top: position.y,
                                ...itemStyle,
                            }}
                        >
                            {renderItem(item, index)}
                        </VirtualGalleryItem>
                    );
                })}
            </div>

            {/* 无限滚动检测器 */}
            {enableInfiniteScroll && (
                <InfiniteScrollDetector
                    onLoadMore={onLoadMore}
                    loadingMore={loadingMore}
                    hasMore={hasMore}
                    threshold={loadMoreThreshold}
                    loadingContent={loadingMoreContent || defaultLoadingContent}
                    noMoreContent={noMoreContent}
                />
            )}
        </div>
    );
};
